import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { X, Megaphone } from "lucide-react";

/**
 * Fetches superadmin broadcasts targeted at in-app channels and renders any
 * that this user hasn't dismissed yet. Sits at the top of AppShell so it is
 * visible on every page.
 */
export default function BroadcastBanner() {
    const [items, setItems] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/broadcasts/active");
            setItems(data.items || []);
        } catch (_) { /* not logged in yet, or endpoint offline — silently skip */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    const dismiss = async (id) => {
        setItems((prev) => prev.filter((b) => b.id !== id));
        try { await api.post(`/broadcasts/${id}/dismiss`); } catch (_) { /* best effort */ }
    };

    if (!items.length) return null;

    return (
        <div className="flex flex-col gap-2 border-b border-sand-200" data-testid="broadcast-banner">
            {items.map((b) => (
                <div key={b.id} className="bg-gradient-to-r from-secondary/10 to-ocean-50 border-b border-secondary/20 px-5 lg:px-10 py-3 flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 h-8 w-8 rounded-full bg-white grid place-items-center text-secondary shadow-sm">
                        <Megaphone className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-heading text-base text-ocean-700">{b.title}</div>
                        {/* body_html trusted — authored by superadmin only */}
                        <div className="prose prose-sm max-w-none text-ocean-700/90 mt-0.5" dangerouslySetInnerHTML={{ __html: b.body_html }} />
                    </div>
                    {b.dismissible !== false && (
                        <button
                            onClick={() => dismiss(b.id)}
                            className="shrink-0 text-muted-foreground hover:text-ocean-700 p-1 rounded-md hover:bg-white/60 transition-colors"
                            data-testid={`broadcast-dismiss-${b.id}`}
                            aria-label="Dismiss announcement"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
